/**
 * RestoID page chrome — matches the Claude Design "RestoID Experience" 1:1:
 *   - the aurora glow (5 layers: grain + static base + swayA/swayB crossfade + bloom)
 *   - the top header (RU/EN switch + a light/dark/system theme dropdown — the logo
 *     lives inside the card, see BrandHead)
 *   - the bottom footer (Privacy · Data Consent · Support · ©)
 *
 * Rendered as plain DOM appended to <body> so it stays fully decoupled from the
 * (pristine) upstream React tree. The keyframes it references (restoSwayA/B,
 * restoBloom, restoFadeIn) live in custom/theme/restoid.css. Imported for its
 * side effect from src/App.tsx.
 */
const FONT =
  '"Inter", -apple-system, system-ui, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
/* Theme tokens (custom/theme/restoid.css), with the current dark value inlined as
   the var() fallback so the chrome still paints correctly if the stylesheet has not
   applied yet — the same contract as the `C` object in components/restoid.tsx. They
   stay plain CSS strings (not `C` members) so this plain-DOM module keeps its zero
   imports; var() is valid both inside the `style="…"` strings built below and in the
   `el.style.color = …` assignments in syncLang/mkLink. */
const TEXT = 'var(--rid-text, #f3f4f5)';
const FAINT = 'var(--rid-faint, rgba(243,244,245,0.36))';
const SUB = 'var(--rid-sub, rgba(243,244,245,0.54))';
const LINE = 'var(--rid-line, rgba(255,255,255,0.10))';

type Lang = 'ru' | 'en';

/**
 * The chrome FOLLOWS the experience's resolved language: react-i18next sets
 * `<html lang>` to it once resolved (a MutationObserver re-syncs us on change).
 * `?lng` is only an early hint before that happens. Default RU (RestoID is RU-first).
 */
const currentLang = (): Lang => {
  try {
    const htmlLang = (document.documentElement.lang || '').toLowerCase();
    if (htmlLang.startsWith('en')) {
      return 'en';
    }
    if (htmlLang.startsWith('ru')) {
      return 'ru';
    }
    const q = new URLSearchParams(location.search).get('lng');
    if (q) {
      return q.toLowerCase().startsWith('en') ? 'en' : 'ru';
    }
  } catch {
    /* Noop */
  }
  return 'ru';
};

const setLang = (lng: string): void => {
  try {
    const url = new URL(location.href);
    url.searchParams.set('lng', lng);
    location.assign(url.toString());
  } catch {
    /* Noop */
  }
};

type ThemePref = 'system' | 'light' | 'dark';

/**
 * The theme preference lives in the blocking script in index.html's <head>, which
 * resolves it into `html[data-rid-theme]` BEFORE first paint — that is what makes a
 * reload keep the chosen theme with no flash. This module only drives it; it never
 * owns the value, and it deliberately reads the attribute rather than localStorage
 * so the two can never disagree.
 */
type ThemeApi = {
  get: () => ThemePref;
  resolved: () => 'light' | 'dark';
  set: (pref: ThemePref) => void;
};

declare global {
  /* Augmenting the global Window needs declaration merging, and only an interface
     can do that — hence the one-off rule exception below. */
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __restoidTheme?: ThemeApi;
  }
}

const themeApi = (): ThemeApi | undefined => window.__restoidTheme;

const currentThemePref = (): ThemePref => {
  const fromApi = themeApi()?.get();
  if (fromApi) {
    return fromApi;
  }
  const attribute = document.documentElement.dataset.ridThemePref;
  return attribute === 'light' || attribute === 'dark' ? attribute : 'system';
};

/* Lucide, 24-box, stroke currentColor — same set and conventions as components/restoid.tsx. */
const THEME_ICON: Record<ThemePref, string> = {
  system:
    '<rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/>',
  light:
    '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
  dark: '<path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/>',
};

const THEME_LABEL: Record<ThemePref, { ru: string; en: string }> = {
  system: { ru: 'Как в системе', en: 'System' },
  light: { ru: 'Светлая', en: 'Light' },
  dark: { ru: 'Тёмная', en: 'Dark' },
};

const THEME_TRIGGER_LABEL = { ru: 'Тема оформления', en: 'Theme' } as const;

const THEME_ORDER: readonly ThemePref[] = ['light', 'dark', 'system'];

/** A tick, shown against the selected option. */
const CHECK_ICON = '<path d="M20 6 9 17l-5-5"/>';

const COPY = {
  privacy: { ru: 'Политика конфиденциальности', en: 'Privacy Policy' },
  terms: { ru: 'Согласие на обработку данных', en: 'Data Consent' },
  support: { ru: 'Поддержка', en: 'Support' },
} as const;

/** The aurora glow — 5 layers, 1:1 with the design (grain + base + swayA/B + bloom). */
const mountAurora = (): void => {
  if (document.querySelector('#restoid-aurora')) {
    return;
  }
  const aurora = document.createElement('div');
  aurora.id = 'restoid-aurora';
  // A fixed-size band anchored to the TOP of the page (absolute, not fixed): it
  // scrolls with the document so it never slides onto the form, and px sizing keeps
  // it identical regardless of window height. The bright centre sits above the band
  // top (clipped), so only the soft lower fade shows — as on the original design.
  aurora.setAttribute(
    'style',
    'position:absolute;top:0;left:0;right:0;height:760px;z-index:-1;pointer-events:none;overflow:hidden'
  );
  // Every color below is its own `--rid-aurora-*` token: a lime glow over a white
  // page needs entirely different values, so these are the most theme-sensitive
  // colors in the product and none of them may share a token with anything else.
  // The `#000` in the two mask gradients is NOT a color — it is the opaque stop of
  // an alpha mask, identical in every theme — so it stays a literal.
  aurora.innerHTML = `
    <div style="position:absolute;inset:0;opacity:0.6;background-image:radial-gradient(circle, var(--rid-aurora-grain, rgba(214,232,150,0.05)) 1px, transparent 1.3px);background-size:34px 34px;-webkit-mask-image:radial-gradient(ellipse 80% 60% at 50% -4%, #000 18%, transparent 72%);mask-image:radial-gradient(ellipse 80% 60% at 50% -4%, #000 18%, transparent 72%)"></div>
    <div style="position:absolute;top:-400px;left:50%;width:min(1320px,112vw);height:940px;margin-left:calc(min(1320px,112vw) * -0.5);background:radial-gradient(ellipse 56% 50% at 50% 30%, var(--rid-aurora-base, rgba(176,207,47,0.15)), transparent 68%)"></div>
    <div style="position:absolute;top:-450px;left:41%;width:1000px;height:880px;margin-left:-500px;transform-origin:50% 0%;will-change:opacity,transform;background:radial-gradient(ellipse 50% 52% at 50% 28%, var(--rid-aurora-sway-a, rgba(176,207,47,0.32)), transparent 62%);animation:restoSwayA 16s ease-in-out infinite"></div>
    <div style="position:absolute;top:-450px;left:59%;width:1000px;height:880px;margin-left:-500px;transform-origin:50% 0%;will-change:opacity,transform;background:radial-gradient(ellipse 50% 52% at 50% 28%, var(--rid-aurora-sway-b, rgba(120,180,48,0.26)), transparent 62%);animation:restoSwayB 16s ease-in-out infinite"></div>
    <div style="position:absolute;top:-235px;left:50%;width:660px;height:480px;margin-left:-330px;transform-origin:50% 0%;will-change:opacity,transform;background:radial-gradient(ellipse 50% 58% at 50% 34%, var(--rid-aurora-bloom, rgba(201,227,98,0.30)), transparent 64%);animation:restoBloom 7.5s ease-in-out infinite"></div>`;
  document.body.prepend(aurora);
};

const mkLink = (href: string): HTMLAnchorElement => {
  const a = document.createElement('a');
  a.href = href;
  a.setAttribute(
    'style',
    `color:${FAINT};text-decoration:none;cursor:pointer;pointer-events:auto;transition:color .15s ease`
  );
  a.addEventListener('mouseenter', () => {
    a.style.color = SUB;
  });
  a.addEventListener('mouseleave', () => {
    a.style.color = FAINT;
  });
  return a;
};

const dot = (): HTMLSpanElement => {
  const s = document.createElement('span');
  s.textContent = '·';
  return s;
};

const mount = (): void => {
  mountAurora();

  if (document.querySelector('#restoid-chrome')) {
    return;
  }

  const root = document.createElement('div');
  root.id = 'restoid-chrome';

  // ---- header: RU/EN switch only, right-aligned (the logo is the card's head) ----
  const header = document.createElement('header');
  header.setAttribute(
    'style',
    // Position, inset and z-index live in custom/theme/restoid.css so a media query
    // can switch them: the bar is only FIXED on wide screens. On a narrow one it
    // scrolls away with the page instead of hovering over the form.
    'display:flex;align-items:center;justify-content:flex-end;' +
      'padding:22px 26px;pointer-events:none;'
  );
  const langBox = document.createElement('div');
  // A pill with its own ground and a blur: the header is fixed, so page content
  // scrolls UNDER these controls and would otherwise run straight into them.
  langBox.setAttribute(
    'style',
    'display:inline-flex;align-items:center;gap:1px;pointer-events:auto;' +
      'padding:2px 4px;border-radius:12px;' +
      `background:var(--rid-chrome-pill);border:1px solid ${LINE};` +
      'backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);' +
      'box-shadow:var(--rid-shadow-1)'
  );
  const langButtons: Array<{ code: Lang; el: HTMLButtonElement }> = [];
  for (const [i, code] of (['ru', 'en'] as const).entries()) {
    if (i === 1) {
      const sep = document.createElement('span');
      sep.setAttribute('style', `width:1px;height:12px;background:${LINE}`);
      langBox.append(sep);
    }
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = code.toUpperCase();
    b.style.cssText = `padding:5px 7px;background:none;border:none;cursor:pointer;letter-spacing:0.04em;transition:color .15s ease`;
    b.addEventListener('click', () => setLang(code));
    langBox.append(b);
    langButtons.push({ code, el: b });
  }

  // ---- theme: a three-option dropdown (light / dark / system) ----
  // A cycle button would hide two of the three states behind guesswork, so this is
  // a real listbox: trigger + menu, closed on Escape, outside click and selection.
  const themeSep = document.createElement('span');
  themeSep.setAttribute('style', `width:1px;height:12px;background:${LINE};margin:0 4px`);
  langBox.append(themeSep);

  const themeWrap = document.createElement('div');
  themeWrap.setAttribute('style', 'position:relative;display:inline-flex');

  const themeButton = document.createElement('button');
  themeButton.type = 'button';
  themeButton.setAttribute('aria-haspopup', 'listbox');
  themeButton.setAttribute('aria-expanded', 'false');
  themeButton.style.cssText =
    'display:inline-flex;align-items:center;gap:4px;padding:5px 6px;background:none;' +
    'border:none;cursor:pointer;transition:color .15s ease';

  const themeMenu = document.createElement('div');
  themeMenu.setAttribute('role', 'listbox');
  themeMenu.style.cssText =
    'position:absolute;top:calc(100% + 8px);right:0;z-index:1;display:none;' +
    'min-width:168px;padding:5px;border-radius:12px;' +
    `background:var(--rid-overlay-2);border:1px solid ${LINE};` +
    'box-shadow:var(--rid-shadow-2);';

  const optionButtons: Array<{ pref: ThemePref; el: HTMLButtonElement }> = [];
  for (const pref of THEME_ORDER) {
    const option = document.createElement('button');
    option.type = 'button';
    option.setAttribute('role', 'option');
    option.style.cssText =
      'display:flex;align-items:center;gap:9px;width:100%;padding:8px 10px;' +
      'border:none;border-radius:8px;background:none;cursor:pointer;text-align:left;' +
      'transition:background .15s ease';
    option.addEventListener('mouseenter', () => {
      option.style.background = 'var(--rid-card-hover)';
    });
    option.addEventListener('mouseleave', () => {
      option.style.background = 'none';
    });
    option.addEventListener('click', () => {
      themeApi()?.set(pref);
      closeMenu();
      syncTheme();
    });
    themeMenu.append(option);
    optionButtons.push({ pref, el: option });
  }

  let menuOpen = false;
  function closeMenu(): void {
    menuOpen = false;
    themeMenu.style.display = 'none';
    themeButton.setAttribute('aria-expanded', 'false');
  }
  const openMenu = (): void => {
    menuOpen = true;
    themeMenu.style.display = 'block';
    themeButton.setAttribute('aria-expanded', 'true');
  };

  const syncTheme = (): void => {
    const pref = currentThemePref();
    const lang = currentLang();
    // Faint while following the system, full strength once the visitor has taken
    // the decision over — the state is readable without opening the menu.
    themeButton.style.color = pref === 'system' ? FAINT : TEXT;
    const trigger = `${THEME_TRIGGER_LABEL[lang]}: ${THEME_LABEL[pref][lang].toLowerCase()}`;
    themeButton.title = trigger;
    themeButton.setAttribute('aria-label', trigger);
    themeButton.innerHTML =
      `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
      `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">${THEME_ICON[pref]}</svg>` +
      `<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
      `stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="opacity:.6">` +
      `<path d="m6 9 6 6 6-6"/></svg>`;
    for (const { pref: optionPref, el } of optionButtons) {
      const selected = optionPref === pref;
      el.setAttribute('aria-selected', String(selected));
      el.style.color = selected ? TEXT : SUB;
      el.innerHTML =
        `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ` +
        `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto">` +
        `${THEME_ICON[optionPref]}</svg>` +
        `<span style="flex:1;font:${selected ? '600' : '500'} 13px/1.3 ${FONT}">` +
        `${THEME_LABEL[optionPref][lang]}</span>` +
        (selected
          ? `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--rid-brand)" ` +
            `stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex:0 0 auto">` +
            `${CHECK_ICON}</svg>`
          : '<span style="width:14px;flex:0 0 auto"></span>');
    }
  };

  themeButton.addEventListener('click', (event) => {
    event.stopPropagation();
    if (menuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  });
  document.addEventListener('click', (event) => {
    const { target } = event;
    if (menuOpen && target instanceof Node && !themeWrap.contains(target)) {
      closeMenu();
    }
  });
  document.addEventListener('keydown', (event) => {
    if (menuOpen && event.key === 'Escape') {
      closeMenu();
      themeButton.focus();
    }
  });

  themeWrap.append(themeButton, themeMenu);
  langBox.append(themeWrap);

  header.append(langBox);

  // ---- footer: Privacy · Data Consent · Support · © ----
  const footer = document.createElement('footer');
  // In normal flow (#restoid-chrome is the last body child) so it sits BELOW the
  // form and scrolls with the page; the sticky-footer column (restoid.css) keeps it
  // pinned to the bottom when the form is short.
  footer.setAttribute(
    'style',
    'position:relative;display:flex;align-items:center;' +
      'justify-content:center;gap:16px;flex-wrap:wrap;padding:22px 16px;pointer-events:none;' +
      `color:${FAINT};font:400 12px/1.5 ${FONT}`
  );
  const span = document.createElement('span');
  span.setAttribute(
    'style',
    'display:inline-flex;align-items:center;gap:9px;flex-wrap:wrap;justify-content:center'
  );
  const aPrivacy = mkLink('/privacy');
  const aTerms = mkLink('/terms');
  const aSupport = mkLink('mailto:id@resto.capital');
  const copyright = document.createElement('span');
  copyright.textContent = '© 2026 RestoCapital';
  span.append(aPrivacy, dot(), aTerms, dot(), aSupport, dot(), copyright);
  footer.append(span);

  root.append(header, footer);
  document.body.append(root);

  // Keep chrome language in sync with the experience's resolved language.
  const syncLang = (): void => {
    const lang = currentLang();
    aPrivacy.textContent = COPY.privacy[lang];
    aTerms.textContent = COPY.terms[lang];
    aSupport.textContent = COPY.support[lang];
    for (const { code, el } of langButtons) {
      const on = lang === code;
      el.style.color = on ? TEXT : FAINT;
      el.style.font = `${on ? '600' : '500'} 12.5px/1 ${FONT}`;
    }
  };
  syncLang();
  syncTheme();
  try {
    new MutationObserver(syncLang).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang'],
    });
    // The head script re-resolves the attribute when the OS flips while the
    // preference is "system"; mirror that into the button.
    new MutationObserver(syncTheme).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-rid-theme', 'data-rid-theme-pref', 'lang'],
    });
  } catch {
    /* Noop */
  }

  dismissBootLoader();
};

/**
 * Fade out + remove the index.html "Welcome to RestoID" boot loader once the app
 * has actually rendered a page (or after a safety timeout, so it can never stick).
 */
const dismissBootLoader = (): void => {
  const boot = document.querySelector('#restoid-boot');
  if (!boot) {
    return;
  }
  const hide = (): void => {
    boot.classList.add('restoid-boot-hide');
    setTimeout(() => boot.remove(), 600);
  };
  if (document.querySelector('.logto_page-container')) {
    hide();
    return;
  }
  const observer = new MutationObserver(() => {
    if (document.querySelector('.logto_page-container')) {
      observer.disconnect();
      hide();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // Never leave the loader stuck if a page somehow renders without that container.
  setTimeout(() => {
    observer.disconnect();
    hide();
  }, 10_000);
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount, { once: true });
  } else {
    mount();
  }
}

export {};
